import {useSelector, useDispatch} from 'react-redux';

import {IframeModal} from 'shells/iframe/iframe-modal';
import {QuoteAndInvoiceInfoModal} from 'shells/modal/quote-and-invoice-info-modal';
import {YouTubeModal} from 'shells/video/youtube-modal';

import type {NutshellState} from '../../store';

import {ConnectedFormsInfoModal} from '../connected-forms/onboarding/connected-forms-info-modal';
import {EmbedAnalyticsModal} from '../email-marketing/analytics/embed-analytics-modal';
import {BroadcastInfoModal} from '../email-marketing/broadcasts/onboarding/broadcast-info-modal';
import {CompetitorIqInfoModal} from '../email-marketing/competitors/onboarding-modal/competitor-iq-info-modal';
import {DripSequenceInfoModal} from '../email-marketing/drip-sequences/onboarding/drip-sequence-info-modal';
import {NewsletterInfoModal} from '../email-marketing/newsletters/onboarding/newsletter-info-modal';
import {CampaignsCurtainModal} from '../email-marketing/onboarding/onboarding-curtain/campaigns-curtain-modal';
import {VisitorIQOnboardingModal} from '../email-marketing/visitors/onboarding-modal/visitor-iq-onboarding-modal';
import {AdTechFXModal} from '../entity-list/footer/adtech-fx-modal';
import {FormsInfoModal} from '../forms/onboarding/forms-info-modal';
import {ChatbotLearnMoreModal} from '../inbox/chat/chatbot-learn-more-modal/chatbot-learn-more-modal';
import {ChatbotThreadsLearnMoreModal} from '../inbox/chat/chatbot-threads-learn-more-modal/chatbot-threads-learn-more-modal';
import {EngagementBarOnboardingModal} from '../inbox/chat/engagement-bar-modal/engagement-bar-onboarding-modal';
import {AgentStatusInfoModal} from '../inbox/chat/settings/agents/agent-status-info-modal';
import {EmbedChatModal} from '../inbox/chat/settings/embed-chat-modal';
import {SharedEmailInboxLearnMoreModal} from '../inbox/email/shared-email-inbox-learn-more-modal/shared-email-inbox-learn-more-modal';
import {ThreadListSidebarWrapper} from '../inbox/shared-components/thread-list-sidebar/thread-list-sidebar-wrapper';
import {ThreadSidebarWrapper} from '../inbox/shared-components/thread-sidebar-wrapper';
import {SmsSidebarWrapper} from '../inbox/sidebar/sms/sms-sidebar-wrapper';
import {LandingPagesInfoModal} from '../landing-pages/onboarding/landing-pages-info-modal';
import {HelpLeadRecaps} from '../lead/onboarding/help-lead-recaps/help-lead-recaps';
import {PipelinesFeatureGateModal} from '../limits/feature-gates/pipelines-feature-gate-modal';
import {InviteUsersModal} from '../onboarding/onboarding-checklist/invite-users-modal';
import {ActivityLogModal} from '../peep/activities/activity-log-modal/activity-log-modal';
import {FormSubmissionModalWrapper} from '../peep/form-submissions/form-submission-modal-wrapper';
import {PeepRedesignOnboardingModal} from '../peep/onboarding/peep-redesign-onboarding-modal';
import {PeepRedesignSwapperModal} from '../peep/onboarding/peep-redesign-swapper-modal';
import {EngagementReportOnboardingModal} from '../reports/engagement/onboarding/engagement-report-onboarding-modal';
import {QuotasV2OnboardingModal} from '../sales/quotas/quotas-v2-onboarding-modal';
import {CreatePipelineModalWrapper} from '../sales-automation/home/create-pipeline-modal-wrapper';
import {BoardOnboarding} from '../sales-automation/onboarding/board-onboarding/board-onboarding';
import {Wizard} from '../sales-automation/onboarding/wizard/wizard';
import {SchedulerOnboardingModal} from '../scheduler/onboarding/scheduler-onboarding-modal';
import {AnalyticsOnboardingModal} from '../setup/analytics/analytics-onboarding-modal';
import {ImportModal} from '../setup/import/import-modal/import-modal';
import {AttributionOnboardingModal} from '../setup/sources/onboarding/attribution-onboarding-modal';

import {OpenableModals} from './openable-modals';
import type {OpenableModal} from './openable-modals';
import * as actions from './openable-modals-actions';
import * as selectors from './openable-modals-selectors';

export function OpenableModalsWrapper() {
    const openModals = useSelector((state: NutshellState) => selectors.getOpenModals(state));
    const dispatch = useDispatch();

    return (
        <>
            {openModals.map((openModal: OpenableModal) => {
                switch (openModal.id) {
                    case OpenableModals.ACTIVITY_LOG_MODAL:
                        if (!openModal.options) {
                            return null;
                        }

                        return (
                            <ActivityLogModal
                                key={OpenableModals.ACTIVITY_LOG_MODAL}
                                activityId={openModal.options.activityId}
                                entityType={openModal.options.entityType}
                                onLogActivity={openModal.options.onLogActivity}
                            />
                        );
                    case OpenableModals.ADTECH_FX_INTEREST_MODAL:
                        return (
                            <AdTechFXModal
                                canDismiss={
                                    openModal.options ? openModal.options.canDismiss : false
                                }
                                key={OpenableModals.ADTECH_FX_INTEREST_MODAL}
                            />
                        );
                    case OpenableModals.ANALYTICS_ONBOARDING_MODAL:
                        return (
                            <AnalyticsOnboardingModal
                                key={OpenableModals.ANALYTICS_ONBOARDING_MODAL}
                            />
                        );
                    case OpenableModals.ATTRIBUTION_ONBOARDING_MODAL:
                        return (
                            <AttributionOnboardingModal
                                key={OpenableModals.ATTRIBUTION_ONBOARDING_MODAL}
                            />
                        );
                    case OpenableModals.BOARD_ONBOARDING_MODAL:
                        return (
                            <BoardOnboarding
                                key={OpenableModals.BOARD_ONBOARDING_MODAL}
                                onClose={() =>
                                    dispatch(
                                        actions.closeModal(OpenableModals.BOARD_ONBOARDING_MODAL)
                                    )
                                }
                                shouldShowWelcome={false}
                            />
                        );
                    case OpenableModals.BROADCAST_INFO_MODAL:
                        return <BroadcastInfoModal key={OpenableModals.BROADCAST_INFO_MODAL} />;
                    case OpenableModals.CAMPAIGNS_ONBOARDING_MODAL:
                        return (
                            <CampaignsCurtainModal
                                key={OpenableModals.CAMPAIGNS_ONBOARDING_MODAL}
                            />
                        );
                    case OpenableModals.CHATBOT_LEARN_MORE_MODAL:
                        return (
                            // @ts-expect-error
                            <ChatbotLearnMoreModal key={OpenableModals.CHATBOT_LEARN_MORE_MODAL} />
                        );
                    case OpenableModals.CHATBOT_THREADS_LEARN_MORE_MODAL:
                        return (
                            // @ts-expect-error
                            <ChatbotThreadsLearnMoreModal
                                key={OpenableModals.CHATBOT_THREADS_LEARN_MORE_MODAL}
                            />
                        );
                    case OpenableModals.COMPETITOR_IQ_INFO_MODAL:
                        return (
                            <CompetitorIqInfoModal key={OpenableModals.COMPETITOR_IQ_INFO_MODAL} />
                        );
                    case OpenableModals.CONNECTED_FORMS_INFO_MODAL:
                        return (
                            <ConnectedFormsInfoModal
                                key={OpenableModals.CONNECTED_FORMS_INFO_MODAL}
                            />
                        );
                    case OpenableModals.CREATE_PIPELINE_MODAL:
                        return (
                            <CreatePipelineModalWrapper
                                key={OpenableModals.CREATE_PIPELINE_MODAL}
                            />
                        );
                    case OpenableModals.DESIGN_PIPELINE_WIZARD:
                        return <Wizard key={OpenableModals.DESIGN_PIPELINE_WIZARD} />;
                    case OpenableModals.DRIP_SEQUENCE_INFO_MODAL:
                        return (
                            <DripSequenceInfoModal key={OpenableModals.DRIP_SEQUENCE_INFO_MODAL} />
                        );
                    case OpenableModals.EMBED_ANALYTICS_MODAL:
                        return <EmbedAnalyticsModal key={OpenableModals.EMBED_ANALYTICS_MODAL} />;
                    case OpenableModals.EMBED_CHAT_MODAL:
                        return <EmbedChatModal />;
                    case OpenableModals.FORMS_INFO_MODAL:
                        return <FormsInfoModal key={OpenableModals.FORMS_INFO_MODAL} />;
                    case OpenableModals.FORM_SUBMISSION_MODAL:
                        if (!openModal.options?.submissionId) {
                            return null;
                        }

                        return (
                            <FormSubmissionModalWrapper
                                key={OpenableModals.FORM_SUBMISSION_MODAL}
                                submissionId={openModal.options?.submissionId}
                                primaryEntity={openModal.options?.primaryEntity}
                                submissionTime={openModal.options?.submissionTime}
                                form={openModal.options.form}
                            />
                        );
                    case OpenableModals.HELP_LEAD_RECAPS_MODAL:
                        return (
                            <HelpLeadRecaps
                                key={OpenableModals.HELP_LEAD_RECAPS_MODAL}
                                isOpen={true}
                                onClose={() =>
                                    dispatch(
                                        actions.closeModal(OpenableModals.HELP_LEAD_RECAPS_MODAL)
                                    )
                                }
                            />
                        );
                    case OpenableModals.IFRAME_MODAL:
                        return (
                            <IframeModal
                                key={OpenableModals.IFRAME_MODAL}
                                url={openModal.options ? openModal.options.url : ''}
                                title={openModal.options ? openModal.options.title : ''}
                                // We pass the onClose into YouTubeModal since it's a shell so we want to keep hooks out of it.
                                onClose={() =>
                                    dispatch(actions.closeModal(OpenableModals.IFRAME_MODAL))
                                }
                            />
                        );
                    case OpenableModals.IMPORT_MODAL:
                        return <ImportModal key={OpenableModals.IMPORT_MODAL} />;
                    case OpenableModals.SMS_SIDEBAR:
                        return (
                            <SmsSidebarWrapper
                                key={OpenableModals.SMS_SIDEBAR}
                                entityId={openModal.options ? openModal.options.entityId : ''}
                                openedFromEntityName={
                                    openModal.options ? openModal.options.openedFromEntityName : ''
                                }
                                openedFromPhoneNumber={
                                    openModal.options
                                        ? openModal.options.openedFromPhoneNumber
                                        : null
                                }
                                channelId={
                                    openModal.options ? openModal.options.channelId : undefined
                                }
                                threadId={
                                    openModal.options ? openModal.options.threadId : undefined
                                }
                            />
                        );
                    case OpenableModals.THREAD_LIST_SIDEBAR:
                        return (
                            <ThreadListSidebarWrapper
                                key={OpenableModals.THREAD_LIST_SIDEBAR}
                                entityId={openModal.options?.entityId}
                                openedFromEntityName={openModal.options?.openedFromEntityName}
                                threadIds={openModal.options ? openModal.options.threadIds : []}
                                title={openModal.options ? openModal.options.title : ''}
                                subtitle={openModal.options ? openModal.options.subtitle : ''}
                            />
                        );
                    case OpenableModals.THREAD_SIDEBAR:
                        return (
                            <ThreadSidebarWrapper
                                key={OpenableModals.THREAD_SIDEBAR}
                                entityId={openModal.options ? openModal.options.entityId : ''}
                                openedFromEntityName={
                                    openModal.options ? openModal.options.openedFromEntityName : ''
                                }
                                channelId={
                                    openModal.options ? openModal.options.channelId : undefined
                                }
                                threadId={
                                    openModal.options ? openModal.options.threadId : undefined
                                }
                            />
                        );
                    case OpenableModals.AGENT_STATUS_INFO_MODAL:
                        return (
                            // @ts-expect-error
                            <AgentStatusInfoModal key={OpenableModals.AGENT_STATUS_INFO_MODAL} />
                        );
                    case OpenableModals.INVITE_USERS_MODAL:
                        return <InviteUsersModal key={OpenableModals.INVITE_USERS_MODAL} />;
                    case OpenableModals.LANDING_PAGES_INFO_MODAL:
                        return (
                            <LandingPagesInfoModal key={OpenableModals.LANDING_PAGES_INFO_MODAL} />
                        );
                    case OpenableModals.NEWSLETTER_INFO_MODAL:
                        return <NewsletterInfoModal key={OpenableModals.NEWSLETTER_INFO_MODAL} />;
                    case OpenableModals.PEEP_REDESIGN_ONBOARDING_MODAL:
                        return (
                            <PeepRedesignOnboardingModal
                                key={OpenableModals.PEEP_REDESIGN_ONBOARDING_MODAL}
                                manualOpening={true}
                            />
                        );
                    case OpenableModals.PEEP_REDESIGN_SWAPPER_MODAL:
                        return (
                            <PeepRedesignSwapperModal
                                key={OpenableModals.PEEP_REDESIGN_SWAPPER_MODAL}
                            />
                        );
                    case OpenableModals.PIPELINES_FEATURE_GATE_MODAL:
                        return (
                            <PipelinesFeatureGateModal
                                key={OpenableModals.PIPELINES_FEATURE_GATE_MODAL}
                            />
                        );
                    case OpenableModals.QUOTES_AND_INVOICES_ONBOARDING_MODAL:
                        return (
                            <QuoteAndInvoiceInfoModal
                                key={OpenableModals.QUOTES_AND_INVOICES_ONBOARDING_MODAL}
                                isOpen={true}
                                onClose={() =>
                                    dispatch(
                                        actions.closeModal(
                                            OpenableModals.QUOTES_AND_INVOICES_ONBOARDING_MODAL
                                        )
                                    )
                                }
                            />
                        );
                    case OpenableModals.QUOTAS_V2_ONBOARDING_MODAL:
                        return (
                            <QuotasV2OnboardingModal
                                key={OpenableModals.QUOTAS_V2_ONBOARDING_MODAL}
                            />
                        );
                    case OpenableModals.SCHEDULER_ONBOARDING_MODAL:
                        return (
                            <SchedulerOnboardingModal
                                key={OpenableModals.SCHEDULER_ONBOARDING_MODAL}
                            />
                        );
                    case OpenableModals.VISITOR_IQ_ONBOARDING_MODAL:
                        return (
                            <VisitorIQOnboardingModal
                                key={OpenableModals.VISITOR_IQ_ONBOARDING_MODAL}
                            />
                        );
                    case OpenableModals.YOUTUBE_MODAL:
                        return (
                            <YouTubeModal
                                key={OpenableModals.YOUTUBE_MODAL}
                                videoId={openModal.options ? openModal.options.videoId : ''}
                                // We pass the onClose into YouTubeModal since it's a shell so we want to keep hooks out of it.
                                onClose={() =>
                                    dispatch(actions.closeModal(OpenableModals.YOUTUBE_MODAL))
                                }
                            />
                        );
                    case OpenableModals.SHARED_EMAIL_INBOX_LEARN_MORE_MODAL:
                        return (
                            // @ts-expect-error
                            <SharedEmailInboxLearnMoreModal
                                key={OpenableModals.SHARED_EMAIL_INBOX_LEARN_MORE_MODAL}
                            />
                        );
                    case OpenableModals.ENGAGEMENT_BAR_ONBOARDING_MODAL:
                        return (
                            // @ts-expect-error
                            <EngagementBarOnboardingModal
                                key={OpenableModals.ENGAGEMENT_BAR_ONBOARDING_MODAL}
                            />
                        );
                    case OpenableModals.ENGAGEMENT_REPORT_ONBOARDING_MODAL:
                        return (
                            // @ts-expect-error
                            <EngagementReportOnboardingModal
                                key={OpenableModals.ENGAGEMENT_REPORT_ONBOARDING_MODAL}
                            />
                        );
                    default:
                        return null;
                }
            })}
        </>
    );
}
